import type { IntakeTemplate } from '../templateTypes';

export type Client2026RowDef = {
  axe: number;
  question: string;
  example?: string;
  defaultA?: 'N' | 'E' | 'NE';
};

// Colonnes du questionnaire (A,B,C,D,E,F,H,I)
export const CLIENT_2026_HEADERS = {
  A: 'Client (N)ouveau et/ou (E)xistant',
  B: 'STO',
  C: 'Pilier / section historique',
  D: 'Problèmes/besoins/souhaits/menaces/soucis/améliorations',
  E: 'Exemples de réponses',
  F: 'Réponses du client',
  H: 'Priorité selon client',
  I: 'Priorité selon Directeur TI de BZ',
} as const;

export const CLIENT_2026_ALL_COL_DEFS: Array<{ key: string; label: string; always?: boolean }> = [
  { key: 'A', label: 'Client (N)ouveau et/ou (E)xistant' },
  { key: 'B', label: 'STO' },
  { key: 'C', label: 'Pilier / section historique', always: true },
  { key: 'D', label: 'Problèmes/besoins/souhaits/menaces/soucis/améliorations', always: true },
  { key: 'E', label: 'Exemples de réponses' },
  { key: 'F', label: 'Réponses du client' },
  { key: 'G', label: 'Comment on sait si le problème est réglé?' },
  { key: 'H', label: 'Priorité selon client' },
  { key: 'I', label: 'Priorité selon Directeur TI de BZ' },
  { key: 'J', label: 'Objectifs SMART' },
  { key: 'K', label: 'Solutions compréhensives (Technologique)' },
  { key: 'L', label: 'Solutions compréhensives (Processus)' },
  { key: 'M', label: 'Solutions compréhensives (Humain)' },
  { key: 'N', label: 'Coûts (Prix BZ)' },
  { key: 'O', label: 'Cout chargé au client' },
  { key: 'P', label: 'Contraintes de temps' },
  { key: 'Q', label: 'À faire dans la prochaine année' },
  { key: 'R', label: 'Exécution int/ext.' },
  { key: 'S', label: "Durée de l'effort" },
  { key: 'T', label: 'Risques' },
  { key: 'U', label: 'Approches de mitigation' },
  { key: 'V', label: 'Besoins' },
  { key: 'W', label: 'Subvention disponible' },
];

export function buildLegacyClient2026ExportRow(
  def: Client2026RowDef,
  readValue: (columnLabel: string) => string,
): string[] {
  return CLIENT_2026_ALL_COL_DEFS.map((column) => {
    if (column.key === 'A') return readValue(column.label) || def.defaultA || '';
    if (column.key === 'C') return String(def.axe);
    if (column.key === 'D') return def.question;
    if (column.key === 'E') return def.example || '';
    return readValue(column.label);
  });
}

export function buildLegacyClient2026DocumentRows(
  responses: Record<string, unknown>,
): Array<Record<string, string>> {
  const axeIndexes = new Map<number, number>();
  return CLIENT_2026_ROWS.map((def) => {
    const index = (axeIndexes.get(def.axe) || 0) + 1;
    axeIndexes.set(def.axe, index);
    const qid = `client2026_a${def.axe}_${String(index).padStart(2, '0')}`;
    return Object.fromEntries(CLIENT_2026_ALL_COL_DEFS.map((column) => {
      if (column.key === 'C') return ['C', String(def.axe)];
      if (column.key === 'D') return ['D', def.question];
      if (column.key === 'E') return ['E', def.example || ''];
      if (column.key === 'A') {
        return ['A', String(responses[`${qid}__A`] ?? responses[qid] ?? def.defaultA ?? '')];
      }
      return [column.key, String(responses[`${qid}__${column.key}`] ?? '')];
    }));
  });
}

export function selectLegacyClient2026DocumentColumns(
  rows: Array<Record<string, string>>,
): typeof CLIENT_2026_ALL_COL_DEFS {
  return CLIENT_2026_ALL_COL_DEFS.filter((column) =>
    column.always || rows.some((row) => {
      const value = String(row[column.key] ?? '').trim();
      return value !== '' && value !== '-';
    })
  );
}

export const CLIENT_2026_AXE_TITLES: Record<number, string> = {
  1: 'Gouvernance, stratégie & organisation',
  2: 'Architecture, infrastructure & cloud',
  3: 'Applications, données & intégrations',
  4: 'Services, opérations & expérience utilisateur',
  5: 'Cybersécurité, risque & conformité',
  6: 'Transformation, innovation & IA',
  7: 'Transformation numérique',
  8: 'Autres',
};

// Questions = colonne D, exemples = colonne E, par Axe (colonne C)
export const CLIENT_2026_ROWS: Client2026RowDef[] = [
  { axe: 1, question: "Avez-vous une gouvernance (roles & resp.) défini/documenté/communiqué?", defaultA: 'NE' },
  { axe: 1, question: "Avez-vous un plan stratégique d'affaires?", defaultA: 'NE' },
  { axe: 1, question: "Avez-vous des indicateurs de performance d'affaires?", defaultA: 'NE' },
  { axe: 1, question: 'Avez-vous un comité TI', defaultA: 'N' },
  { axe: 1, question: 'À quelle fréquence sont les rencontres', defaultA: 'NE' },
  { axe: 1, question: 'À quelle fréquence souhaitez-vous avoir un suivi sur les tâches en cours', defaultA: 'NE' },
  { axe: 1, question: 'Avez-vous un budget TI annuel?', defaultA: 'NE' },
  { axe: 1, question: 'Avez-vous des dépenses TI autres que celles avec BZ', defaultA: 'E' },
  {
    axe: 1,
    question:
      "Avez-vous un plan stratégique en TI, qui est aligné avec le plan stratégique d'affaires, et avec des priorités claires?",
    defaultA: 'NE',
  },
  { axe: 1, question: 'Avez-vous un plan TI annuel (roadmap) détaillé?', defaultA: 'NE' },
  {
    axe: 1,
    question:
      "-Avez-vous des améliorations que vous souhaitez/planifiez/documentez mettre en œuvre au sein de votre entreprise?",
    example:
      "gestion de projets, gestion de connaissances, optimisation de processus/procédures d'affaires et/ou TI, amélioration de la posture de sécurité, gestion documents, rendement, retention de personnel, pénurie de main-d'œuvre",
    defaultA: 'NE',
  },

  { axe: 2, question: 'Avez-vous un inventaire à jour de vos actifs TI (équipements)', defaultA: 'N' },
  {
    axe: 2,
    question: 'Est-ce que vos actifs TI (équipements) sont adéquats pour vos besoins d\'affaires actuels',
    example:
      'besoins storage, besoins computationnels, sauvegarde externe, commutateur désuet, pare-feu, cablage réseau, bande passante',
    defaultA: 'NE',
  },
  {
    axe: 2,
    question: 'Est-ce que vos actifs TI sont adéquats pour vos besoins d\'affaires futurs (5ans)',
    example: "projet d'expansion/acquisition, nouveau site/bureau, nouveau marché",
    defaultA: 'NE',
  },

  { axe: 3, question: 'Avez-vous un inventaire à jour de vos actifs TI (Applications/données)', defaultA: 'N' },
  {
    axe: 3,
    question: 'Est-ce que vos actifs TI (Applications/données) sont adéquats pour vos besoins d\'affaires actuels',
    example: 'Gestion des licences, logiciels spécialisés, gestion des données personnelles',
    defaultA: 'NE',
  },
  {
    axe: 3,
    question: 'Est-ce que vos actifs TI (Applications/données) sont adéquats pour vos besoins d\'affaires futurs (5ans)',
    example: "projet d'expansion/acquisition, nouveau site/bureau, nouveau marché",
    defaultA: 'NE',
  },

  { axe: 4, question: 'Avez-vous des enjeux de disponibilités de vos actifs TI', example: 'lenteur, coupure', defaultA: 'NE' },
  {
    axe: 4,
    question: "Avez-vous des enjeux d'intégrité de vos actifs TI",
    example: 'intrusion ou erreur humaine menant à la corruption de données',
    defaultA: 'NE',
  },
  {
    axe: 4,
    question: 'Avez-vous des enjeux de confidentialité de vos actifs TI',
    example: "exigences spécifiques du secteur d'affaire (ex. avocat, comptables, domaine médical…)",
    defaultA: 'NE',
  },
  {
    axe: 4,
    question: 'Avez-vous des enjeux de traçabilité de vos actifs TI',
    example: "Démontrer l'historique de l'état des données (ex. signature, journalisation/log)",
    defaultA: 'NE',
  },
  {
    axe: 4,
    question: 'Est-ce que les usagers sont habilités à exploiter de façon optimum les actifs TI',
    example: 'Les employés sont-ils formés adéquatement',
    defaultA: 'NE',
  },
  {
    axe: 4,
    question:
      'Est-ce que les processus/procédures sont adéquats/ajustés pour faciliter la pleine exploitation des actifs TI',
    example: "Existe-t-il des politiques décrivant ce qui est permis de faire avec les actifs TI",
    defaultA: 'NE',
  },
  { axe: 4, question: 'Avez-vous des enjeux de soutien techniques pour vos usagers', defaultA: 'NE' },

  { axe: 5, question: 'Avez-vous une gouvernance pour prendre des décisions sur les enjeux de cybersécurité et conformité', defaultA: 'NE' },
  {
    axe: 5,
    question: 'Avez-vous des enjeux de conformité',
    example: 'Loi 25, ISO 9001, SOC 2, exigences des assurances, ordre prof.',
    defaultA: 'NE',
  },
  {
    axe: 5,
    question: 'Avez-vous bien identifier les actifs TI existants qui requiert des actions en cybersécurité',
    example: 'Inventaires des actifs TI, Rédaction de politiques, Score Microsoft, audit de sécurité, Évaluation des risques',
    defaultA: 'NE',
  },
  {
    axe: 5,
    question:
      "Avez-vous déterminer et appliquer les mesures de protection adéquates pour sécuriser vos actifs TI et mettre à l'abri les valeurs et la réputation de l'entreprise",
    example: "Plan de saugarde, plan de formation, campagne d'hameçonnage, balayage de vulnérabilités, test d'intrusion",
    defaultA: 'NE',
  },
  {
    axe: 5,
    question:
      'Avez-vous déterminer et appliquer les mesures de détection adéquates pour notifier le plus rapidement possible les menaces et intrusions',
    example: 'Anti-virus, SIEM, SOC',
    defaultA: 'NE',
  },
  {
    axe: 5,
    question:
      'Avez-vous déterminer et appliquer les mesures de réponse adéquates pour mettre fin et controler les effets néfastes de l’incident',
    example: "Politiques en cas d'incidents, plan de reprise",
    defaultA: 'NE',
  },
  {
    axe: 5,
    question:
      "Avez-vous déterminer et appliquer les mesures de récupération adéquates pour corriger les effets néfastes et remettre l'environnement TI dans un état opérationnel et permettre la reprise des activités d'affaire",
    example: "Politiques en cas d'incidents, plan de reprise",
    defaultA: 'NE',
  },

  {
    axe: 6,
    question:
      "Avez-vous des enjeux de transformation numérique en lien avec l'automatisation de certaines tâches/processus simples et répétitives?",
    example:
      "élimination des documents papiers, télé-travail, application de l'IA pour l'automatisation, IA générative pour la gestion de connaissance, pallier au manque de main d'œuvre",
    defaultA: 'NE',
  },
  {
    axe: 7,
    question:
      "Avez-vous des enjeux de transformation numérique en soutien aux employés/décideurs pour augmenter la performance et l'efficacité de ceux-ci?",
    example:
      "exploitation de la technologie Agentik (exécution semi-autonome) à la gouvernance/exploitation, gestion de carrière via un agent IA",
    defaultA: 'NE',
  },
  { axe: 8, question: "Autres besoins spécifiques exprimés par le client (ex. gestion efficace des réunions,…)", defaultA: 'NE' },
];

/**
 * Anciens libellés de la colonne « Problèmes/besoins/… » (D) pour la même ligne logique.
 * Les documents Markdown déjà enregistrés peuvent encore contenir ces textes : l’export Excel
 * les associe à la ligne courante du gabarit.
 */
export const CLIENT_2026_QUESTION_MARKDOWN_ALIASES: Readonly<Record<string, readonly string[]>> = {
  "Est-ce que vos actifs TI (équipements) sont adéquats pour vos besoins d'affaires actuels": [
    'Est-ce que vos actifs TI (équipements) sont adéquats pour vos besoins opérationnelles actuels',
  ],
  "Est-ce que vos actifs TI sont adéquats pour vos besoins d'affaires futurs (5ans)": [
    'Est-ce que vos actifs TI sont adéquats pour vos besoins opérationnelles futures (5ans)',
    "Est-ce que vos actifs TI sont adéquats pour vos besoins d'affaires futures (5ans)",
  ],
  "Est-ce que vos actifs TI (Applications/données) sont adéquats pour vos besoins d'affaires actuels": [
    'Est-ce que vos actifs TI (Applications/données) sont adéquats pour vos besoins opérationnelles actuels',
  ],
  "Est-ce que vos actifs TI (Applications/données) sont adéquats pour vos besoins d'affaires futurs (5ans)": [
    'Est-ce que vos actifs TI (Applications/données) sont adéquats pour vos besoins opérationnelles futures (5ans)',
    "Est-ce que vos actifs TI (Applications/données) sont adéquats pour vos besoins d'affaires futures (5ans)",
  ],
};

export function findClient2026MarkdownRow(
  def: Client2026RowDef,
  rowByQuestion: Map<string, string[]>,
  normalizeKey: (s: string) => string
): string[] | undefined {
  const tried = new Set<string>();
  const candidates = [def.question, ...(CLIENT_2026_QUESTION_MARKDOWN_ALIASES[def.question] ?? [])];
  for (const c of candidates) {
    const k = normalizeKey(c);
    if (tried.has(k)) continue;
    tried.add(k);
    const row = rowByQuestion.get(k);
    if (row) return row;
  }
  return undefined;
}

const makeId = (axe: number, idxInAxe: number) => `client2026_a${axe}_${String(idxInAxe).padStart(2, '0')}`;

const makePhase = (axe: number, rows: Client2026RowDef[], overrides?: Partial<{ id: string; title: string; description: string }>) => ({
  id: overrides?.id ?? `axe${axe}`,
  title: overrides?.title ?? `Pilier ${axe} — ${CLIENT_2026_AXE_TITLES[axe] || ''}`,
  description: overrides?.description ?? `Répondez aux questions du pilier ${axe}.`,
  icon: 'FileText',
  questions: rows.map((r, idx) => ({
    id: makeId(axe, idx + 1),
    label: r.question,
    type: 'text' as const,
    helpText: r.example ? `Exemples: ${r.example}` : undefined,
    defaultValue: r.defaultA ? r.defaultA : undefined,
  })),
});

// Phases:
// - 1 à 5: inchangées
// - 6: combine les axes 6 et 7
// - 7: axe 8 (optionnel côté UI)
const phases = (() => {
  const p1to5 = Array.from({ length: 5 }, (_, i) => i + 1).map((axe) =>
    makePhase(axe, CLIENT_2026_ROWS.filter((r) => r.axe === axe)),
  );

  const rows6 = CLIENT_2026_ROWS.filter((r) => r.axe === 6);
  const rows7 = CLIENT_2026_ROWS.filter((r) => r.axe === 7);
  const combined6Questions = [
    ...rows6.map((r, idx) => ({
      id: makeId(6, idx + 1),
      label: r.question,
      type: 'text' as const,
      helpText: r.example ? `Exemples: ${r.example}` : undefined,
      defaultValue: r.defaultA ? r.defaultA : undefined,
    })),
    ...rows7.map((r, idx) => ({
      id: makeId(7, idx + 1),
      label: r.question,
      type: 'text' as const,
      helpText: r.example ? `Exemples: ${r.example}` : undefined,
      defaultValue: r.defaultA ? r.defaultA : undefined,
    })),
  ];

  const phase6 = {
    id: 'axe6',
    title: `Pilier 6 — ${CLIENT_2026_AXE_TITLES[6]}`,
    description: 'Les catégories historiques 6 et 7 regroupent les questions de ce pilier; les réponses restent inchangées.',
    icon: 'FileText',
    questions: combined6Questions,
  };

  const phase7 = makePhase(8, CLIENT_2026_ROWS.filter((r) => r.axe === 8), {
    id: 'axe8',
    title: 'Autres — section historique',
    description: 'Section optionnelle — à activer si nécessaire.',
  });

  return [...p1to5, phase6, phase7];
})();

export const legacyTemplatePriseDeBesoinClient2026: IntakeTemplate = {
  id: 'prise-de-besoin-client-2026',
  name: 'Prise de besoin client 2026',
  description:
    'Questionnaire historique structuré par pilier, avec une section Autres distincte (colonne C). Les questions proviennent de la colonne D, les exemples de la colonne E, et les champs à remplir alimentent les colonnes F, H et I du document généré.',
  documentTypeCode: 'PRISE_BESOIN_CLIENT_2026',
  version: '2.0.1',
  phases,
};